import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  MagnifyingGlass, ChartLineUp, Lightning, ArrowRight,
  Star, Robot, Target, Gauge, CheckCircle, ArrowUpRight
} from '@phosphor-icons/react';

/* ─── Design tokens ─────────────────────────────────────────── */
const C = {
  bg:       '#0a0a0f',
  surface:  '#13131f',
  surfaceHi:'#181828',
  border:   'rgba(124,58,237,0.2)',
  borderHi: 'rgba(124,58,237,0.45)',
  violet:   '#7c3aed',
  blue:     '#3b82f6',
  text:     '#f1f5f9',
  muted:    '#94a3b8',
  faint:    '#475569',
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:   '0 4px 24px rgba(124,58,237,0.1)',
  shadowHi: '0 8px 32px rgba(124,58,237,0.22)',
  font:     "'Inter', system-ui, -apple-system, sans-serif",
};

const btnGrad = {
  background:  C.gradient,
  color:       '#fff',
  border:      'none',
  borderRadius: 10,
  fontWeight:  700,
  cursor:      'pointer',
  fontFamily:  C.font,
  display:     'inline-flex',
  alignItems:  'center',
  gap:         8,
  textDecoration: 'none',
  transition:  'opacity 0.15s, box-shadow 0.15s',
};

const btnOutline = {
  background:  'transparent',
  color:       C.muted,
  border:      '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  fontWeight:  600,
  cursor:      'pointer',
  fontFamily:  C.font,
  display:     'inline-flex',
  alignItems:  'center',
  gap:         6,
  textDecoration: 'none',
  transition:  'all 0.15s',
};

/* ─── Data ───────────────────────────────────────────────────── */
const FEATURES = [
  { icon: MagnifyingGlass, color:'#7c3aed', title:'SEO Audit',          desc:'Full site audit in 30 seconds. Scores for SEO, performance, Core Web Vitals. Export professional PDF reports.' },
  { icon: ChartLineUp,     color:'#3b82f6', title:'Rank Tracker',        desc:'Track Google positions daily. Monitor up to 2,000 keywords automatically. Alerts when positions drop.' },
  { icon: Lightning,       color:'#10b981', title:'Backlink Monitor',     desc:'Monitor all your backlinks 24/7. Instant alerts when a link is lost. Track dofollow/nofollow and anchor text.' },
  { icon: Robot,           color:'#a78bfa', title:'AI Traffic Engine',    desc:'Rewrite titles, score content, generate keywords, detect duplicates — all powered by AI.' },
  { icon: Target,          color:'#f59e0b', title:'Link Builder',         desc:'Submit to 20+ quality directories. Generate personalized outreach emails. Discover competitor backlinks.' },
  { icon: Gauge,           color:'#ec4899', title:'Action Center',        desc:'Crawl your entire site, find SEO issues, export corrections as CSV, SQL, or PDF in one click.' },
];

const STEPS = [
  { n:'01', title:'Audit your site',      desc:'Enter any URL. Get a full SEO audit with scores, Core Web Vitals, and AI-powered fix suggestions in 30 seconds.' },
  { n:'02', title:'Track rankings',       desc:'Add your keywords. Every night at 3AM, we check your Google positions and alert you if anything drops.' },
  { n:'03', title:'Monitor backlinks',    desc:'Import your backlinks. We verify them daily at 4AM and notify you instantly if a link is lost.' },
];

const STATS = [
  { value:'50K+',  label:'Sites analyzed' },
  { value:'2.5M',  label:'Issues fixed' },
  { value:'98%',   label:'Satisfaction rate' },
  { value:'< 30s', label:'Analysis time' },
];

const PLANS = [
  {
    id: 'free', name: 'Free', price: '€0', period: '/month',
    desc: 'Perfect to get started',
    features: ['1 audit / day','5 keywords tracked','10 backlinks monitored','Basic SEO report'],
    cta: 'Start free', popular: false,
  },
  {
    id: 'pro', name: 'Pro', price: '€29', period: '/month',
    desc: 'For serious SEO professionals',
    features: ['Unlimited audits','500 keywords tracked','Unlimited backlinks','AI Traffic Engine','PDF export','Priority support'],
    cta: 'Get Pro', popular: true,
  },
  {
    id: 'agency', name: 'Agency', price: '€79', period: '/month',
    desc: 'For teams and agencies',
    features: ['Everything in Pro','10 client sites','White-label reports','API access','Team members (5)','Dedicated support'],
    cta: 'Contact us', popular: false,
  },
];

const TESTIMONIALS = [
  { quote: "The Fix Tracker feature is a game-changer. Instead of just showing problems, it gives me the exact code I need. My workflow is 10x faster.", name:'Sarah Chen', role:'SEO Lead, TechCorp' },
  { quote: "Finally, an SEO tool that speaks my language. The AI suggestions are surprisingly accurate and the interface is incredibly clean.", name:'Marcus Johnson', role:'Digital Marketing Director' },
];

/* ═══════════════════════════════════════════════════════════════
   LandingPage
═══════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [url, setUrl] = useState('');

  const handleAnalyze = () => {
    if (!url.trim()) return;
    const dest = isAuthenticated
      ? `/dashboard?url=${encodeURIComponent(url)}`
      : `/login?url=${encodeURIComponent(url)}`;
    navigate(dest);
  };

  /* ── RENDER ───────────────────────────────────────────────── */
  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:C.font, color:C.text }}>

      {/* ── NAVBAR ────────────────────────────────────────── */}
      <nav style={{
        background:'rgba(10,10,15,0.9)',
        backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)',
        borderBottom:`1px solid ${C.border}`,
        position:'sticky', top:0, zIndex:50,
      }}>
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 28px', height:58, display:'flex', alignItems:'center', gap:0 }}>

          {/* Logo */}
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:9, textDecoration:'none', marginRight:40, flexShrink:0 }}>
            <div style={{ width:32, height:32, background:C.gradient, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, boxShadow:'0 2px 12px rgba(124,58,237,0.4)' }}>
              ✦
            </div>
            <span style={{ fontWeight:800, fontSize:16, letterSpacing:'-0.5px', color:C.text }}>
              SEO Dash <span style={{ color:C.violet }}>360</span>
            </span>
          </Link>

          {/* Nav */}
          <div style={{ display:'flex', alignItems:'center', gap:2, flex:1 }}>
            {[['#features','Features'],['#pricing','Pricing'],['/guide','Guide']].map(([href,label]) => (
              href.startsWith('#')
                ? <a key={href} href={href} style={{ padding:'6px 13px', borderRadius:8, fontSize:13, fontWeight:500, color:C.muted, textDecoration:'none', transition:'all 0.15s' }}
                    onMouseEnter={e=>{ e.currentTarget.style.color=C.text; e.currentTarget.style.background='rgba(255,255,255,0.05)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.color=C.muted; e.currentTarget.style.background='transparent'; }}>{label}</a>
                : <Link key={href} to={href} style={{ padding:'6px 13px', borderRadius:8, fontSize:13, fontWeight:500, color:C.muted, textDecoration:'none', transition:'all 0.15s' }}
                    onMouseEnter={e=>{ e.currentTarget.style.color=C.text; e.currentTarget.style.background='rgba(255,255,255,0.05)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.color=C.muted; e.currentTarget.style.background='transparent'; }}>{label}</Link>
            ))}
          </div>

          {/* Auth buttons */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {isAuthenticated ? (
              <Link to="/dashboard" style={{ ...btnGrad, padding:'8px 18px', fontSize:13, borderRadius:9 }}>
                Dashboard <ArrowRight style={{ width:13, height:13 }} />
              </Link>
            ) : (
              <>
                <Link to="/login" style={{ ...btnOutline, padding:'8px 16px', fontSize:13, borderRadius:9 }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.text; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,0.12)'; e.currentTarget.style.color=C.muted; }}>
                  Login
                </Link>
                <Link to="/login" style={{ ...btnGrad, padding:'8px 18px', fontSize:13, borderRadius:9, boxShadow:'0 2px 16px rgba(124,58,237,0.3)' }}>
                  Start Free ✦
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{ padding:'96px 28px 80px', textAlign:'center', position:'relative', overflow:'hidden' }}>
        {/* Glow orbs */}
        <div style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:700, height:400, background:'radial-gradient(ellipse at center, rgba(124,58,237,0.15) 0%, transparent 70%)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', top:'30%', left:'15%', width:300, height:300, background:'radial-gradient(ellipse at center, rgba(59,130,246,0.08) 0%, transparent 70%)', pointerEvents:'none' }} />

        <div style={{ maxWidth:760, margin:'0 auto', position:'relative' }}>
          {/* Badge pill */}
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(124,58,237,0.1)', border:`1px solid rgba(124,58,237,0.3)`, borderRadius:99, padding:'6px 16px', marginBottom:28 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#10b981', display:'inline-block', boxShadow:'0 0 8px #10b981' }} />
            <span style={{ fontSize:12, fontWeight:700, color:'#a78bfa', letterSpacing:'0.05em', textTransform:'uppercase' }}>AI-Powered SEO Platform</span>
          </div>

          {/* H1 */}
          <h1 style={{ fontSize:56, fontWeight:900, margin:'0 0 16px', letterSpacing:'-2px', lineHeight:1.08, color:C.text }}>
            Turn SEO problems into<br />
            <span style={{ background:C.gradient, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
              copy-paste solutions
            </span>
          </h1>

          {/* Subtitle */}
          <p style={{ fontSize:18, color:C.muted, margin:'0 0 44px', lineHeight:1.65, maxWidth:580, marginLeft:'auto', marginRight:'auto' }}>
            Audit, fix, and optimize automatically.{' '}
            <strong style={{ color:C.text, fontWeight:600 }}>What agencies charge €2,000 for, done in 30 seconds.</strong>
          </p>

          {/* URL input */}
          <div style={{ display:'flex', gap:10, maxWidth:580, margin:'0 auto 14px' }}>
            <div style={{ flex:1, position:'relative' }}>
              <MagnifyingGlass style={{ position:'absolute', left:15, top:'50%', transform:'translateY(-50%)', width:15, height:15, color:C.faint }} />
              <input
                type="url" value={url} onChange={e => setUrl(e.target.value)}
                placeholder="https://yoursite.com"
                onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                style={{ width:'100%', padding:'13px 14px 13px 44px', background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, fontSize:14, color:C.text, outline:'none', boxSizing:'border-box', fontFamily:C.font, transition:'border-color 0.2s' }}
                onFocus={e => e.target.style.borderColor = C.violet}
                onBlur={e => e.target.style.borderColor = C.border}
              />
            </div>
            <button onClick={handleAnalyze}
              style={{ ...btnGrad, padding:'13px 24px', fontSize:14, borderRadius:10, whiteSpace:'nowrap', boxShadow:'0 4px 20px rgba(124,58,237,0.35)' }}>
              Analyze now <ArrowRight style={{ width:14, height:14 }} />
            </button>
          </div>
          <p style={{ fontSize:12, color:C.faint }}>Free analysis · No credit card required</p>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────── */}
      <section style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ maxWidth:1200, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(4,1fr)' }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ textAlign:'center', padding:'28px 16px', borderRight:i<3?`1px solid ${C.border}`:'none' }}>
              <p style={{ fontSize:32, fontWeight:900, margin:0, letterSpacing:'-1px', background:C.gradient, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
                {s.value}
              </p>
              <p style={{ fontSize:12, color:C.muted, margin:'5px 0 0', fontWeight:500, textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────── */}
      <section id="features" style={{ padding:'96px 28px' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:56 }}>
            <p style={{ fontSize:11, fontWeight:700, color:C.violet, letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 12px' }}>Features</p>
            <h2 style={{ fontSize:40, fontWeight:900, margin:'0 0 12px', letterSpacing:'-1.2px', color:C.text }}>
              Everything you need to dominate search
            </h2>
            <p style={{ fontSize:16, color:C.muted, margin:0 }}>One platform. All the tools you need to grow organic traffic.</p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {FEATURES.map((f, i) => (
              <div key={i}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:'26px 24px', boxShadow:C.shadow, transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=C.borderHi; e.currentTarget.style.boxShadow=C.shadowHi; e.currentTarget.style.background=C.surfaceHi; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow=C.shadow; e.currentTarget.style.background=C.surface; }}>
                <div style={{ width:44, height:44, background:`${f.color}18`, border:`1px solid ${f.color}35`, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                  <f.icon weight="bold" style={{ width:20, height:20, color:f.color }} />
                </div>
                <h3 style={{ fontSize:16, fontWeight:700, color:C.text, margin:'0 0 10px', letterSpacing:'-0.3px' }}>{f.title}</h3>
                <p style={{ fontSize:13, color:C.muted, margin:0, lineHeight:1.65 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────── */}
      <section style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'88px 28px' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:56 }}>
            <p style={{ fontSize:11, fontWeight:700, color:C.violet, letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 12px' }}>How it works</p>
            <h2 style={{ fontSize:40, fontWeight:900, margin:0, letterSpacing:'-1.2px', color:C.text }}>Three steps to better rankings</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:32 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ position:'relative', paddingTop:20 }}>
                <span style={{ fontSize:80, fontWeight:900, color:'rgba(124,58,237,0.08)', position:'absolute', top:-20, left:-8, lineHeight:1, userSelect:'none', pointerEvents:'none' }}>{s.n}</span>
                <div style={{ position:'relative', paddingLeft:4 }}>
                  <div style={{ width:36, height:36, background:'rgba(124,58,237,0.12)', border:`1px solid rgba(124,58,237,0.25)`, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14, fontSize:13, fontWeight:800, color:C.violet }}>
                    {i + 1}
                  </div>
                  <h3 style={{ fontSize:17, fontWeight:700, color:C.text, margin:'0 0 10px', letterSpacing:'-0.3px' }}>{s.title}</h3>
                  <p style={{ fontSize:13, color:C.muted, margin:0, lineHeight:1.7 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────── */}
      <section style={{ padding:'88px 28px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <p style={{ fontSize:11, fontWeight:700, color:C.violet, letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 12px' }}>Testimonials</p>
            <h2 style={{ fontSize:40, fontWeight:900, margin:0, letterSpacing:'-1.2px', color:C.text }}>Loved by SEO professionals</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i}
                style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:'26px 24px', boxShadow:C.shadow, transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=C.borderHi; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; }}>
                <div style={{ display:'flex', gap:3, marginBottom:14 }}>
                  {[...Array(5)].map((_,j) => <Star key={j} weight="fill" style={{ width:14, height:14, color:'#f59e0b' }} />)}
                </div>
                <p style={{ fontSize:14, color:C.muted, margin:'0 0 20px', lineHeight:1.7 }}>"{t.quote}"</p>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:36, height:36, background:C.gradient, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:14, color:'#fff', flexShrink:0 }}>
                    {t.name[0]}
                  </div>
                  <div>
                    <p style={{ fontSize:13, fontWeight:700, color:C.text, margin:0 }}>{t.name}</p>
                    <p style={{ fontSize:12, color:C.faint, margin:'2px 0 0' }}>{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section id="pricing" style={{ background:C.surface, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'88px 28px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:56 }}>
            <p style={{ fontSize:11, fontWeight:700, color:C.violet, letterSpacing:'0.12em', textTransform:'uppercase', margin:'0 0 12px' }}>Pricing</p>
            <h2 style={{ fontSize:40, fontWeight:900, margin:'0 0 12px', letterSpacing:'-1.2px', color:C.text }}>Start free, scale as you grow</h2>
            <p style={{ fontSize:15, color:C.muted, margin:0 }}>No hidden fees. Cancel anytime.</p>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
            {PLANS.map(plan => (
              <div key={plan.id} style={{
                background: plan.popular ? C.surfaceHi : C.bg,
                border: plan.popular ? `2px solid ${C.violet}` : `1px solid ${C.border}`,
                borderRadius: 20,
                padding: '32px 28px',
                position: 'relative',
                boxShadow: plan.popular ? `0 0 40px rgba(124,58,237,0.2)` : C.shadow,
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
                onMouseEnter={e => { if (!plan.popular) { e.currentTarget.style.borderColor=C.borderHi; e.currentTarget.style.boxShadow=C.shadowHi; } }}
                onMouseLeave={e => { if (!plan.popular) { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow=C.shadow; } }}>

                {plan.popular && (
                  <div style={{ position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:C.gradient, color:'#fff', fontSize:10, fontWeight:800, padding:'4px 16px', borderRadius:99, letterSpacing:'0.08em', whiteSpace:'nowrap', boxShadow:'0 2px 12px rgba(124,58,237,0.4)' }}>
                    MOST POPULAR
                  </div>
                )}

                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:plan.popular ? C.violet : C.faint, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>{plan.name}</div>
                  <div style={{ display:'flex', alignItems:'baseline', gap:4, marginBottom:6 }}>
                    <span style={{ fontSize:40, fontWeight:900, color:C.text, letterSpacing:'-1.5px' }}>{plan.price}</span>
                    <span style={{ fontSize:14, color:C.faint }}>{plan.period}</span>
                  </div>
                  <p style={{ fontSize:13, color:C.muted, margin:0 }}>{plan.desc}</p>
                </div>

                <Link to={plan.id === 'agency' ? '/guide' : '/login'}
                  style={{ ...btnGrad, padding:'11px 0', fontSize:14, borderRadius:10, width:'100%', justifyContent:'center', marginBottom:24, boxShadow: plan.popular ? '0 4px 20px rgba(124,58,237,0.35)' : 'none', opacity: 1 }}>
                  {plan.cta}
                </Link>

                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {plan.features.map((feat, fi) => (
                    <div key={fi} style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                      <CheckCircle weight="fill" style={{ width:16, height:16, color: plan.popular ? C.violet : '#10b981', flexShrink:0, marginTop:1 }} />
                      <span style={{ fontSize:13, color:C.muted }}>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p style={{ textAlign:'center', marginTop:24, fontSize:13, color:C.faint }}>
            Need a custom plan?{' '}
            <Link to="/guide" style={{ color:C.violet, textDecoration:'none', fontWeight:600 }}>Contact us →</Link>
          </p>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────── */}
      <section style={{ padding:'88px 28px', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse at 50% 0%, rgba(124,58,237,0.12) 0%, transparent 70%)', pointerEvents:'none' }} />
        <div style={{ maxWidth:600, margin:'0 auto', textAlign:'center', position:'relative' }}>
          <h2 style={{ fontSize:42, fontWeight:900, margin:'0 0 16px', letterSpacing:'-1.2px', color:C.text }}>
            Ready to improve your rankings?
          </h2>
          <p style={{ fontSize:16, color:C.muted, margin:'0 0 36px', lineHeight:1.65 }}>
            Start with a free analysis. No credit card required.
          </p>
          <Link to={isAuthenticated ? '/dashboard' : '/login'}
            style={{ ...btnGrad, padding:'15px 36px', fontSize:16, borderRadius:12, boxShadow:'0 8px 32px rgba(124,58,237,0.4)' }}>
            Get started free <ArrowRight style={{ width:16, height:16 }} />
          </Link>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer style={{ background:C.surface, borderTop:`1px solid ${C.border}`, padding:'32px 28px' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:20, marginBottom:20 }}>
            {/* Logo */}
            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
              <div style={{ width:28, height:28, background:C.gradient, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>✦</div>
              <span style={{ fontWeight:800, fontSize:15, color:C.text, letterSpacing:'-0.3px' }}>SEO Dash <span style={{ color:C.violet }}>360</span></span>
            </div>
            {/* Nav links */}
            <div style={{ display:'flex', gap:20 }}>
              {[['#features','Features'],['#pricing','Pricing'],['/guide','Guide'],['/login','Login']].map(([href,label]) => (
                href.startsWith('#')
                  ? <a key={href} href={href} style={{ fontSize:13, color:C.faint, textDecoration:'none', transition:'color 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.color=C.muted}
                      onMouseLeave={e=>e.currentTarget.style.color=C.faint}>{label}</a>
                  : <Link key={href} to={href} style={{ fontSize:13, color:C.faint, textDecoration:'none', transition:'color 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.color=C.muted}
                      onMouseLeave={e=>e.currentTarget.style.color=C.faint}>{label}</Link>
              ))}
            </div>
          </div>

          {/* Nos sites */}
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', paddingTop:20, borderTop:`1px solid rgba(255,255,255,0.05)`, justifyContent:'space-between' }}>
            <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:11, color:C.faint, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Nos sites :</span>
              {[
                ['https://leadforgefrance.com/','LeadForge France'],
                ['https://total-depannage.com/','Total Dépannage'],
                ['https://metaverse-virtual-world.com/','Metaverse VW'],
                ['https://cleannumerique.com/','CleanNumerique'],
              ].map(([href,label]) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:12, color:C.faint, textDecoration:'none', display:'flex', alignItems:'center', gap:4, transition:'color 0.15s' }}
                  onMouseEnter={e=>e.currentTarget.style.color=C.muted}
                  onMouseLeave={e=>e.currentTarget.style.color=C.faint}>
                  {label} <ArrowUpRight style={{ width:10, height:10 }} />
                </a>
              ))}
            </div>
            <p style={{ fontSize:12, color:C.faint, margin:0 }}>© 2026 SEO Dash 360. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
